/**
 * Async IndexedDB key-value cache for the LARGE local caches (time entries,
 * projects). These grew to megabytes of JSON — against localStorage's ~5MB
 * quota and re-stringified on the main thread on every debounced persist —
 * so they live in IndexedDB instead. localStorage (via the storage wrapper)
 * remains the home for small hot-path values only.
 *
 * Falls back to the storage wrapper when IndexedDB is unavailable (private
 * browsing, node test environments), so callers never need to branch.
 */

import { localStore as storage } from './localStore';
import { GUN_STORAGE_PREFIX } from '../gunConfig';

// On-disk name — same rule as GUN_DB_NAME: fixed once shipped.
const DB_NAME = `${GUN_STORAGE_PREFIX}_data_cache`;
const STORE = 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;
let idbBroken = false;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'));
    request.onblocked = () => reject(new Error('indexedDB open blocked'));
  });
  dbPromise.catch(() => {
    idbBroken = true;
    dbPromise = null;
  });
  return dbPromise;
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/** Read a value; `defaultValue` when absent or IDB is unavailable AND the storage fallback has nothing. */
export async function idbGet<T>(key: string, defaultValue: T): Promise<T> {
  if (!idbBroken) {
    try {
      const db = await openDb();
      const result = await requestToPromise(tx(db, 'readonly').get(key));
      if (result !== undefined) return result as T;
      return defaultValue;
    } catch {
      idbBroken = true;
    }
  }
  return storage.get(key, defaultValue);
}

/** Write a value. Structured clone, no JSON stringify on the main thread. */
export async function idbSet<T>(key: string, value: T): Promise<void> {
  if (!idbBroken) {
    try {
      const db = await openDb();
      await requestToPromise(tx(db, 'readwrite').put(value, key));
      return;
    } catch {
      idbBroken = true;
    }
  }
  storage.set(key, value);
}

/**
 * Close this module's connection so the database can be DELETED.
 *
 * `deleteDatabase` blocks while any connection is open, and this one is held
 * for the life of the page — so a reset that deletes `<prefix>_data_cache`
 * without calling this always blocked. Worse than failing: a blocked delete
 * stays pending and stalls every later `open()` of that database, so the local
 * store stopped acknowledging writes and Gun's auth never got an ack at all.
 *
 * The next call reopens lazily, so this is safe to call at any time.
 */
export function closeIdbCache(): void {
  const pending = dbPromise;
  dbPromise = null;
  if (!pending) return;
  void pending.then((db) => { try { db.close(); } catch { /* already gone */ } }).catch(() => {});
}

/** Remove a key from the cache (both homes, so cleanup paths need one call). */
export async function idbRemove(key: string): Promise<void> {
  storage.remove(key);
  if (idbBroken) return;
  try {
    const db = await openDb();
    await requestToPromise(tx(db, 'readwrite').delete(key));
  } catch {
    idbBroken = true;
  }
}
